import {
  useMemo,
  useState,
  useEffect,
  useContext,
  ReactElement,
  createContext,
} from "react";
import { ErrorType, SPECIALIST_HOSTNAME } from "env-vars";
import { useAuthenticated } from "hooks/use-authenticated";
import { useTransifexLoader } from "../transifex-loader";
// eslint-disable-next-line react-refresh/only-export-components
export enum Language {
  ENGLISH = "en",
  SPANISH = "es",
  PORTUGUESE = "pt",
}

type PreferredLanguageContextType = {
  preferredLanguage: Language | null;
  setPreferredLanguage: (language: string) => void;
  isLoading: boolean;
  error: ErrorType | null;
  showLanguagePrompt: boolean;
  setShowLanguagePrompt: (show: boolean) => void;
};

const PreferredLanguageContext = createContext<
  PreferredLanguageContextType | undefined
>(undefined);

type Props = {
  children: ReactElement;
};

export function PreferredLanguageProvider({ children }: Props) {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<ErrorType | null>(null);
  const [preferredLanguage, setPreferredLanguage] = useState<Language | null>(
    null
  );
  const [showLanguagePrompt, setShowLanguagePrompt] = useState<boolean>(false);
  const { isAuthenticated } = useAuthenticated();
  const { isLoaded: isTransifexLoaded } = useTransifexLoader();

  // Handle API call to get preferred language
  useEffect(() => {
    if (!isAuthenticated) return;

    (async () => {
      const idToken = localStorage.getItem("id_token");
      const patientId = localStorage.getItem("ciq_id");

      if (!patientId) {
        console.info("patientId is null, cannot fetch preferred language");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${SPECIALIST_HOSTNAME}/api/v1/people/${patientId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!response.ok) {
          console.error("Error fetching preferred language");
          setLoading(false);
          setError(
            response.status === 401 ? ErrorType.AUTH : ErrorType.GENERAL
          );
          return;
        }

        const data = await response.json();
        setPreferredLanguage(data.person.preferred_language);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching preferred language: ", error);
        setError(ErrorType.GENERAL);
        setLoading(false);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    if (preferredLanguage && isTransifexLoaded) {
      const selectedLanguageCode =
        // @ts-ignore not typed
        window.Transifex.live.getSelectedLanguageCode();

      if (preferredLanguage !== selectedLanguageCode) {
        setShowLanguagePrompt(true);
      }
    }
  }, [preferredLanguage, isTransifexLoaded]);

  const contextProps = useMemo(
    () => ({
      isLoading,
      error,
      preferredLanguage,
      setPreferredLanguage,
      showLanguagePrompt,
      setShowLanguagePrompt,
    }),
    [
      isLoading,
      error,
      preferredLanguage,
      showLanguagePrompt,
      setShowLanguagePrompt,
    ]
  );

  return (
    <PreferredLanguageContext.Provider
      value={contextProps as PreferredLanguageContextType}
    >
      {children}
    </PreferredLanguageContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export const usePreferredLanguage = () => {
  const context = useContext(PreferredLanguageContext);

  if (context === undefined) {
    throw new Error(
      "usePreferredLanguage must be used within a PreferredLanguageProvider"
    );
  }

  return context;
};
